import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, ArrowLeft, ArrowRight, Users, Target, Handshake, TrendingUp } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA from '../components/WaitlistCTA';

export default function ForCofounders() {
  const navigate = useNavigate();

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  return (
    <div className="min-h-screen bg-[#F2F6FA]" data-testid="for-cofounders-page">
      <PublicNav currentPage="for-cofounders" />

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-blue-600/10 text-blue-500 rounded-full text-sm font-medium mb-6">
            FOR CO-FOUNDERS
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-6">
            Find founders who share<br />your values and vision.
          </h1>
          <p className="text-lg text-slate-700 mb-8 max-w-3xl mx-auto">
            Connect with founders actively building ventures. Match based on skills and values alignment. Join teams before capital partnerships begin.
          </p>
          <WaitlistCTA 
            variant="founder"
            theme="light"
            size="lg"
            onClick={() => goToWaitlist('founder')}
            testId="find-startups-cta"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card className="bg-white border-slate-200 p-8">
            <Users className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Founders with Commitment</h3>
            <p className="text-slate-700 mb-4 text-sm">Connect with founders who are:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Actively building (not just ideating)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Clear about equity expectations</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Progressing toward capital partnerships</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <Target className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Skills-Based Matching</h3>
            <p className="text-slate-700 mb-4 text-sm">Find complementary co-founders:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Technical seeking business expertise</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Business seeking technical talent</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Product, design, ops specialists</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <Handshake className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Equity-Aligned Expectations</h3>
            <p className="text-slate-700 mb-4 text-sm">Clear expectations from day one:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Filter by equity alignment</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">See time commitment levels</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Vesting clarity upfront</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <TrendingUp className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Join Early-Stage Ventures</h3>
            <p className="text-slate-700 mb-4 text-sm">Meaningful participation potential:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Pre-seed and seed stage ventures</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Shape product from the ground up</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-slate-700 text-sm">Meaningful equity stakes</span>
              </li>
            </ul>
          </Card>
        </div>

        <Card className="bg-white border-slate-200 p-12 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Find Your Co-founding Team?</h2>
          <p className="text-lg text-slate-700 mb-8">
            Join the waitlist and be first to know when SeedSynch opens for co-founder matching.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <WaitlistCTA 
              variant="founder"
              theme="light"
              size="default"
              onClick={() => goToWaitlist('founder')}
            />
            <Link to="/pricing">
              <Button className="btn-secondary text-base px-8 py-5">
                View Pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
